import { useEffect, useRef, useState } from 'react';
import { Box, IconButton, ListItemText, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import { useAIChatContext } from '../context/AIChatContext';
import { useAIChat } from '../hooks/useAIChat';
import { useAssistantRoles } from '../hooks/useAssistantRoles';
import { useViewContext } from '../hooks/useViewContext';
import { AIChatSessionList } from './AIChatSessionList';
import { AIChatMessageBubble } from './AIChatMessage';
import { AIChatInput } from './AIChatInput';
import { AIChatContextBanner } from './AIChatContextBanner';
import { AIChatLimitBanner } from './AIChatLimitBanner';
import { LiveDot } from '../../shared/components/LiveDot';

export function AIChatSidebar() {
  const { width, activeSessionId, sessions, createSession, setSessionTitle } = useAIChatContext();
  const {
    messages,
    loading,
    sending,
    approachingLimit,
    sendMessage,
    cancel,
    regenerate,
    approveToolCall,
    rejectToolCall,
    decideToolCall,
  } = useAIChat(activeSessionId, setSessionTitle);
  const listRef = useRef<HTMLDivElement>(null);
  const { roles } = useAssistantRoles();
  const [newChatAnchor, setNewChatAnchor] = useState<HTMLElement | null>(null);
  // Every role-based assistant's tools are project-scoped (it can't list
  // documents or search the knowledge graph without one) — offering it
  // outside a project would just produce a confused "I have no access" reply.
  const { projectId } = useViewContext();

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const lastAssistant = [...messages].reverse().find((m) => m.role === 'ASSISTANT');
  const canRegenerate = !sending && !!lastAssistant && (lastAssistant.status === 'COMPLETED' || lastAssistant.status === 'FAILED');

  return (
    <Box
      sx={{
        width,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        height: '100%',
        minHeight: 0,
      }}
    >
      <Stack
        sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minHeight: 32 }}>
          <AIChatSessionList />
          <Box sx={{ flex: 1 }} />
          <Tooltip title="New chat">
            <IconButton
              size="small"
              onClick={(e) => (roles.length ? setNewChatAnchor(e.currentTarget) : void createSession())}
            >
              <AddCommentOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            open={!!newChatAnchor}
            anchorEl={newChatAnchor}
            onClose={() => setNewChatAnchor(null)}
            slotProps={{ paper: { sx: { minWidth: 240 } } }}
          >
            <MenuItem
              onClick={() => {
                setNewChatAnchor(null);
                void createSession();
              }}
            >
              <ListItemText primary="General Assistant" secondary="Ask anything, project-wide" />
            </MenuItem>
            {roles.map((role) => (
              <MenuItem
                key={role.key}
                disabled={projectId == null}
                onClick={() => {
                  setNewChatAnchor(null);
                  void createSession(role.key);
                }}
              >
                <ListItemText
                  primary={role.name}
                  secondary={projectId == null ? 'Open a project to use this assistant' : role.description}
                  primaryTypographyProps={{ noWrap: true }}
                  secondaryTypographyProps={{ noWrap: true }}
                />
              </MenuItem>
            ))}
          </Menu>
        </Stack>
        {activeSessionId != null && (
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.25 }}>
            <LiveDot size={6} />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5 }}>
              Connected to workspace context
            </Typography>
          </Stack>
        )}
      </Stack>

      <Box ref={listRef} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 1.5 }}>
        {activeSessionId == null ? (
          <ChatEmptyState hasSessions={sessions.length > 0} onNewChat={() => void createSession()} />
        ) : loading ? (
          <Typography variant="body2" color="text.secondary">Loading…</Typography>
        ) : messages.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Ask a question to get started.</Typography>
        ) : (
          <Stack spacing={1.5}>
            {messages.map((m) => (
              <AIChatMessageBubble
                key={m.id}
                message={m}
                approveToolCall={approveToolCall}
                rejectToolCall={rejectToolCall}
                decideToolCall={decideToolCall}
              />
            ))}
            {canRegenerate && (
              <Tooltip title="Regenerate response">
                <IconButton size="small" onClick={() => void regenerate()} sx={{ alignSelf: 'flex-start' }}>
                  <ReplayIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        )}
      </Box>

      <AIChatLimitBanner approachingLimit={approachingLimit} onNewChat={() => void createSession()} />
      <AIChatContextBanner />
      <AIChatInput
        disabled={activeSessionId == null}
        sending={sending}
        onSend={(content) => void sendMessage(content)}
        onStop={() => void cancel()}
      />
    </Box>
  );
}

function ChatEmptyState({ hasSessions, onNewChat }: { hasSessions: boolean; onNewChat: () => void }) {
  return (
    <Stack spacing={1} alignItems="center" justifyContent="center" sx={{ height: '100%', textAlign: 'center', color: 'text.secondary' }}>
      <Typography variant="body2">
        {hasSessions ? 'Select a chat or start a new one.' : 'Start a conversation with the AI Assistant.'}
      </Typography>
      <IconButton onClick={onNewChat} color="primary">
        <AddCommentOutlinedIcon />
      </IconButton>
    </Stack>
  );
}
